import { canAll, type Access } from './permissions'
import type { AdminContent } from '../types/adminRequests'
import type { Permission } from '../types/team'

/**
 * `PUT /admin/content` stuurt alles in één keer. Per deel geldt een eigen bevoegdheid
 * (docs/LARAVEL.md §11.3): de server vergelijkt met wat er staat en weigert alleen een deel dat
 * **veranderd** is zonder de bevoegdheid. Wie alleen teksten mag, kan zo nooit stil prijzen,
 * instellingen of de catalogus overschrijven. Voorraad telt niet (die slaat apart op, §9.5).
 * Puur en getest; de demo-server gebruikt hetzelfde.
 */

type Part = { label: string; p: Permission; pick: (c: AdminContent) => unknown }

const withoutStock = (c: AdminContent) => c.catalog.products.map(({ stock: _stock, ...p }) => p)

export const CONTENT_PARTS: readonly Part[] = [
  { label: 'Producten', p: 'catalog.edit', pick: withoutStock },
  { label: 'Cadeaubonnen in de winkel', p: 'catalog.edit', pick: (c) => c.catalog.giftCards },
  { label: 'Merken', p: 'catalog.edit', pick: (c) => c.catalog.brands },
  { label: 'Behandelingen', p: 'catalog.edit', pick: (c) => c.catalog.treatments },
  { label: 'Protocollen', p: 'catalog.edit', pick: (c) => c.catalog.routines },
  { label: 'Huidvragen', p: 'catalog.edit', pick: (c) => c.catalog.concerns },
  { label: 'Reviews', p: 'catalog.edit', pick: (c) => c.catalog.reviews },
  { label: 'Journal', p: 'content.edit', pick: (c) => c.catalog.articles },
  { label: 'Teksten', p: 'content.edit', pick: (c) => c.pages },
  { label: 'Instellingen', p: 'settings.edit', pick: (c) => c.site },
  { label: 'Ontwerpen en huisstijl', p: 'marketing.social', pick: (c) => [c.marketing.designs, c.marketing.brandKit] },
  { label: 'Social', p: 'marketing.social', pick: (c) => [c.marketing.posts, c.marketing.ideas] },
  { label: 'Nieuwsbrieven', p: 'marketing.newsletter', pick: (c) => c.marketing.newsletters },
]

const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b)

/** De delen die veranderd zijn en waar dit account geen bevoegdheid voor heeft. Leeg = mag. */
export function deniedContentParts(before: AdminContent, after: AdminContent, access: Access): string[] {
  return CONTENT_PARTS.filter((part) => !canAll(access, part.p) && !same(part.pick(before), part.pick(after))).map((part) => part.label)
}
